import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import Select from "react-select";

import { RootDispatch, RootState } from "../../store";
import Button, { ButtonType } from "../../components/Assets/Button";
import {
  UNITS,
  IWarehouseRawMaterial,
} from "../../models/warehouseRawMaterial.model";
import {
  getDefaultUnitForType,
  getUnitsByType,
} from "../../utils/warehouse.util";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";
import {
  createOrUpdateMaterialBOM,
  createOrUpdateProductBOM,
  getMaterialBOMById,
  getProductBOMById,
} from "../../store/slices/productBOMSlice";
import MaterialAsyncSelect, {
  selectStyles,
} from "../../components/Factory/MaterialSelectAsync";
import * as XLSX from "xlsx-js-style";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { formatToDDMMYYYY } from "../../utils/formatDateUtils";
import CopyBOMModal from "../../components/Product/CopyBomModal";

interface CreateProductBOMProps {
  onSuccess: () => void;
  onCancel: () => void;
  isEdit?: boolean;
  selectedBomId?: string | null;
  selectedProductId?: any;
  title: string;
  from: string;
}

const CreateProductBOM: React.FC<CreateProductBOMProps> = ({
  onSuccess,
  onCancel,
  isEdit = true,
  selectedBomId = null,
  selectedProductId,
  title,
  from,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const handleWheelPrevent = usePreventWheelChange();

  const selectedBom = useSelector(
    (state: RootState) => state.productBOM.selectedBom,
  );
  const loading = useSelector((state: RootState) => state.productBOM.loading);

  const hasFetched = useRef(false);

  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [productId, setProductId] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [componentQuery, setComponentQuery] = useState<string>("");
  const [materialPick, setMaterialPick] = useState<{
    rawMaterial: IWarehouseRawMaterial | null;
    quantity: number;
    unit: string;
  }>({
    rawMaterial: null,
    quantity: 1,
    unit: "",
  });

  const filteredItems = selectedItems.filter((item) =>
    !componentQuery
      ? true
      : (item.name || "")
          .toString()
          .toLowerCase()
          .includes(componentQuery.toLowerCase()) ||
        (item.type || "")
          .toString()
          .toLowerCase()
          .includes(componentQuery.toLowerCase()),
  );

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    if (selectedBomId) {
      if (from === "product") {
        dispatch(getProductBOMById(selectedBomId));
      } else {
        dispatch(getMaterialBOMById(selectedBomId));
      }
    }
  }, [dispatch, from, selectedBomId]);

  useEffect(() => {
    if (selectedProductId) {
      setProductId(selectedProductId);
    }

    if (selectedBom && isEdit) {
      if (selectedBom.productId) setProductId(selectedBom.productId);
      if (selectedBom.materialId) setProductId(selectedBom.materialId);

      const mappedItems =
        selectedBom.components?.map((item: any) => ({
          type: "material",
          id: item.rawMaterial?._id || item.componentId,
          name: item.rawMaterial?.name || "Material",
          quantity: item.quantity,
          unitValue: item.rawMaterial?.unit || item.unit,
          unitLabel:
            UNITS.find((u) => u.value === (item.rawMaterial?.unit || item.unit))
              ?.label || item.unit,
        })) || [];

      setSelectedItems(mappedItems);
    }
  }, [selectedBom, isEdit, selectedProductId]);

  const handleAppendCopiedComponents = (newComponents: any[]) => {
    setSelectedItems((prev) => {
      const updated = [...prev];
      newComponents.forEach((item) => {
        const existingIdx = updated.findIndex(
          (existing) =>
            existing.type === item.type &&
            existing.id === item.id &&
            existing.unitValue === item.unitValue,
        );
        if (existingIdx !== -1) {
          updated[existingIdx] = {
            ...updated[existingIdx],
            quantity: updated[existingIdx].quantity + item.quantity,
          };
        } else {
          updated.push({ ...item });
        }
      });
      return updated;
    });
  };

  const addMaterial = () => {
    const mat = materialPick.rawMaterial;

    if (!mat) {
      return toast.error("Select a material");
    }

    if (!materialPick.unit) {
      return toast.error("Select a unit");
    }

    const existingIndex = selectedItems.findIndex(
      (item) =>
        item.type === "material" &&
        item.id === mat._id &&
        item.unitValue === materialPick.unit,
    );

    const unitLabel =
      UNITS.find((u) => u.value === materialPick.unit)?.label ||
      materialPick.unit;

    if (existingIndex !== -1) {
      const updated = [...selectedItems];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: updated[existingIndex].quantity + materialPick.quantity,
      };
      setSelectedItems(updated);
    } else {
      setSelectedItems((prev) => [
        ...prev,
        {
          type: "material",
          id: mat._id,
          name: mat.name,
          quantity: materialPick.quantity,
          unitValue: materialPick.unit,
          unitLabel,
        },
      ]);
    }

    setMaterialPick({
      rawMaterial: null,
      quantity: 1,
      unit: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productId) {
      return toast.error("Select product");
    }

    if (selectedItems.length === 0) {
      return toast.error("Add at least one component");
    }

    const payload = {
      productBomId: selectedBom?._id,
      productId,
      components: selectedItems.map((item) => ({
        type: item.type === "product" ? "product" : "material",
        productId: item.id,
        quantity: Number(item.quantity),
        unit: item.type === "material" ? item.unitValue : undefined,
      })),
    };

    try {
      if (from === "product") {
        const res = await dispatch(
          createOrUpdateProductBOM({ payload }),
        ).unwrap();
        if (res.success) {
          onSuccess();
        }
      } else {
        await dispatch(createOrUpdateMaterialBOM(payload)).unwrap();
        onSuccess();
      }
    } catch (err) {
      toast.error("Failed to save Product BOM");
    }
  };

  const downloadXLS = () => {
    if (!selectedItems || selectedItems.length === 0) {
      return toast.error("No components to download");
    }

    const data = selectedItems.map((item) => ({
      Name: item.name,
      Quantity: Number(item.quantity),
      Unit:
        item.type === "product"
          ? "Units"
          : item.unitLabel || item.unitValue || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    const sheetName = `BOM-${title}`.slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title}_BOM_${formatToDDMMYYYY(new Date())}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    if (!selectedItems || selectedItems.length === 0) {
      return toast.error("No components to download");
    }

    try {
      const pdfDoc = await PDFDocument.create();
      let page = pdfDoc.addPage([595, 842]); // A4-ish
      let { width, height } = page.getSize();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const marginLeft = 40;
      const marginRight = 40;
      const marginTop = 50;
      const marginBottom = 40;
      const checkboxSize = 10;
      const lineHeight = 20;
      const fontSize = 11;

      // Column layout
      const unitColWidth = 80;
      const qtyColWidth = 60;
      const checkboxX = marginLeft;
      const nameX = checkboxX + checkboxSize + 12;
      const unitX = width - marginRight - unitColWidth + 8;
      const qtyX = unitX - qtyColWidth - 12;
      // Title
      page.drawText(`${title} BOM`, {
        x: marginLeft,
        y: height - marginTop,
        size: 14,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      // Header row
      let y = height - marginTop - 28;
      const headerSize = 12;
      page.drawText("Name", {
        x: nameX,
        y,
        size: headerSize,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      page.drawText("Qty", {
        x: qtyX,
        y,
        size: headerSize,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      page.drawText("Unit", {
        x: unitX,
        y,
        size: headerSize,
        font: boldFont,
        color: rgb(0, 0, 0),
      });

      y -= lineHeight;

      for (let idx = 0; idx < selectedItems.length; idx++) {
        const item = selectedItems[idx];
        const qtyText = String(item.quantity);
        const unitText =
          item.type === "product"
            ? "Units"
            : item.unitLabel || item.unitValue || "";

        // Draw checkbox
        page.drawRectangle({
          x: checkboxX,
          y: y - checkboxSize + 8,
          width: checkboxSize,
          height: checkboxSize,
          borderColor: rgb(0, 0, 0),
          borderWidth: 1,
        });

        // Truncate name to fit
        const nameAvailableWidth = qtyX - nameX - 8;
        let nameText = String(item.name || "");
        while (
          font.widthOfTextAtSize(nameText, fontSize) > nameAvailableWidth &&
          nameText.length > 0
        ) {
          nameText = nameText.slice(0, -1);
        }
        if (nameText !== (item.name || ""))
          nameText = nameText.slice(0, -3) + "...";

        page.drawText(nameText, {
          x: nameX,
          y,
          size: fontSize,
          font,
          color: rgb(0, 0, 0),
        });

        // Qty right aligned
        const qtyWidth = font.widthOfTextAtSize(qtyText, fontSize);
        const qtyDrawX = qtyX + qtyColWidth - qtyWidth - 4;
        page.drawText(qtyText, {
          x: qtyDrawX,
          y,
          size: fontSize,
          font,
          color: rgb(0, 0, 0),
        });

        // Unit
        page.drawText(unitText, {
          x: unitX,
          y,
          size: fontSize,
          font,
          color: rgb(0, 0, 0),
        });

        y -= lineHeight;

        // Page break
        if (y < marginBottom) {
          page = pdfDoc.addPage([595, 842]);
          const size = page.getSize();
          width = size.width;
          height = size.height;
          // redraw title and headers
          page.drawText(`${title} BOM`, {
            x: marginLeft,
            y: height - marginTop,
            size: 14,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
          y = height - marginTop - 28;
          page.drawText("Name", {
            x: nameX,
            y,
            size: headerSize,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
          page.drawText("Qty", {
            x: qtyX,
            y,
            size: headerSize,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
          page.drawText("Unit", {
            x: unitX,
            y,
            size: headerSize,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
          y -= lineHeight;
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([new Uint8Array(pdfBytes)], {
        type: "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title}_BOM_${formatToDDMMYYYY(new Date())}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <div className="relative bg-white rounded-2xl p-4 sm:p-6 min-h-[600px] flex flex-col justify-between">
      {loading && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-2xl">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm font-semibold text-gray-600">
              Saving {title} BOM...
            </p>
          </div>
        </div>
      )}
      <div className="flex justify-end mb-3">
        <Button
          type={ButtonType.Submit}
          onClick={() => setIsCopyModalOpen(true)}
          className="text-xs bg-blue-600 text-white"
        >
          Copy BOM
        </Button>
      </div>
      <div className="space-y-6 flex-1">
        {/* Add Materials Block */}
        <div className="p-4 bg-green-50/50 rounded-xl border border-green-100">
          <label className="block text-xs font-bold text-green-800 mb-3 uppercase tracking-wider">
            Add Raw Materials
          </label>

          <div className="flex flex-col gap-3">
            {/* Modular Reusable Material Select Component */}
            <div className="w-full">
              <MaterialAsyncSelect
                value={materialPick.rawMaterial}
                onChange={(rm) => {
                  setMaterialPick({
                    rawMaterial: rm,
                    quantity: 1,
                    unit: rm ? getDefaultUnitForType(rm.unitType) : "",
                  });
                }}
                from="factory"
              />
            </div>

            {/* Inputs & Add Button */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
              <div className="sm:col-span-3">
                <input
                  type="number"
                  min={1}
                  onWheel={handleWheelPrevent}
                  value={materialPick.quantity}
                  onChange={(e) =>
                    setMaterialPick((p) => ({
                      ...p,
                      quantity: Math.max(1, +e.target.value),
                    }))
                  }
                  placeholder="Qty"
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg h-[42px] outline-none focus:border-green-500"
                />
              </div>

              <div className="sm:col-span-5">
                <Select
                  options={
                    materialPick.rawMaterial
                      ? getUnitsByType(materialPick.rawMaterial.unitType || "")
                      : UNITS
                  }
                  value={
                    UNITS.find((u) => u.value === materialPick.unit) || null
                  }
                  onChange={(opt: any) =>
                    setMaterialPick((p) => ({
                      ...p,
                      unit: opt?.value || "",
                    }))
                  }
                  styles={selectStyles}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  placeholder="Unit"
                />
              </div>

              <div className="sm:col-span-4">
                <Button
                  type={ButtonType.Submit}
                  onClick={addMaterial}
                  className="text-xs h-[42px] w-full shrink-0"
                >
                  Add Material
                </Button>
              </div>
            </div>
          </div>
        </div>
        <CopyBOMModal
          isOpen={isCopyModalOpen}
          onClose={() => setIsCopyModalOpen(false)}
          from={from as "product" | "material"}
          onAppendComponents={handleAppendCopiedComponents}
        />
        {/* Added Components Section with local search */}
        <div className="border border-gray-100 rounded-xl overflow-hidden flex-1 flex flex-col">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 justify-between">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Added Components
              </span>
              <span className="text-xs font-bold text-gray-500">
                {selectedItems.length} items
              </span>
            </div>

            <div className="w-full sm:w-1/3">
              <input
                value={componentQuery}
                onChange={(e) => setComponentQuery(e.target.value)}
                placeholder="Search components"
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg h-[38px] outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <ul className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto bg-white">
            {filteredItems.length === 0 ? (
              <li className="p-6 text-center text-xs text-gray-400">
                {selectedItems.length === 0
                  ? `No ${title} components added yet.`
                  : `No ${title} components match your search.`}
              </li>
            ) : (
              filteredItems.map((item, idx) => (
                <li
                  key={idx}
                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 hover:bg-gray-50/80 gap-3"
                >
                  <div className="flex items-center gap-2 flex-1">
                    <span
                      className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                        item.type === "product"
                          ? "bg-blue-50 text-blue-600"
                          : "bg-green-50 text-green-600"
                      }`}
                    >
                      {item.type}
                    </span>
                    <span className="text-sm font-semibold text-gray-900 truncate">
                      {item.name}
                    </span>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-4">
                    <div className="flex items-center">
                      <input
                        type="number"
                        value={item.quantity}
                        min={1}
                        onWheel={handleWheelPrevent}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setSelectedItems((prev) =>
                            prev.map((it, i) =>
                              i === selectedItems.indexOf(item)
                                ? { ...it, quantity: value }
                                : it,
                            ),
                          );
                        }}
                        className="w-16 text-right text-sm font-bold text-gray-800 border-b border-gray-300 focus:outline-none focus:border-blue-500 py-0.5"
                      />
                      <span className="text-xs font-medium text-gray-500 ml-2">
                        {item.type === "product" ? "Units" : item.unitLabel}
                      </span>
                    </div>

                    <button
                      onClick={() =>
                        setSelectedItems((prev) =>
                          prev.filter(
                            (_, i) => i !== selectedItems.indexOf(item),
                          ),
                        )
                      }
                      className="text-gray-400 hover:text-red-500 p-1 transition-colors"
                      aria-label="Remove item"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      {/* Footer Controls */}
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
        <Button
          type={ButtonType.Cancel}
          onClick={onCancel}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          type={ButtonType.Submit}
          onClick={downloadXLS}
          className="w-full sm:w-auto px-4 bg-gray-200 text-gray-800"
        >
          Download XLS
        </Button>
        <Button
          type={ButtonType.Submit}
          onClick={downloadPDF}
          className="w-full sm:w-auto px-4 bg-gray-200 text-gray-800"
        >
          Download PDF
        </Button>
        <Button
          type={ButtonType.Submit}
          onClick={handleSubmit}
          className="w-full sm:w-auto px-8 bg-navyBlue-primary text-white"
        >
          {isEdit ? "Update BOM" : "Save BOM"}
        </Button>
      </div>
    </div>
  );
};

export default CreateProductBOM;
